<template>
  <div>
    <div v-if="isOrgAdmin && collectEnabled" class="collect-config">
      <router-view />
      <div class="i-mb-14">
        <settings-section-title id="collect" class="i-mb-2"
          >Collect</settings-section-title
        >
        <p class="i-text-body-2 i-text-neutral-110">
          View and update your Collect details
        </p>
      </div>
      <div class="settings-page-block i-py-14 i-section-divide">
        <div class="settings-page-block-title">
          <h5 class="i-text-heading-5 i-text-neutral-120 i-mb-0">Details</h5>
          <p class="i-text-body-2 i-text-neutral-110">
            Manage the details of your Collect requests
          </p>
        </div>
        <div class="settings-page-block-content d-flex flex-column i-gap-10">
          <i-text-input
            :model-value="config.data.display_name"
            label="Display Name"
            placeholder="Your organization name"
            :show-hint-text="true"
            hint-text="Your organization's display name in Collect emails and portal"
            @update:model-value="config.data.display_name = $event"
          ></i-text-input>

          <div class="d-flex flex-column i-gap-4">
            <label>Logo</label>
            <file-upload
              id="org-logo"
              :change-action="uploadCollectConfigLogo"
              :accepted-file-types="acceptedFileTypes"
              :existing-image="config.data.ui.logo_url"
            >
            </file-upload>
            <div class="i-text-body-3 i-text-neutral-110">
              Add your organization's logo
            </div>
          </div>

          <i-text-input
            :model-value="config.data.success_url"
            label="Brand URL"
            placeholder="https://www.example.com"
            :show-hint-text="true"
            hint-text="The URL that the logo links to and the success page navigates to"
            @update:model-value="config.data.success_url = $event"
          ></i-text-input>

          <div class="buttons d-flex align-items-center">
            <i-button
              text="Save"
              data-testid="save-collect-config-button"
              size="lg"
              :disabled="errors || !config.modified"
              @button-click="save()"
            ></i-button>

            <i-button
              text="Cancel"
              type="neutral"
              subtype="subtle"
              data-testid="cancel-collect-config-button"
              size="lg"
              :disabled="!config.modified"
              @button-click="cancel()"
            ></i-button>
          </div>
        </div>
      </div>
      <div class="settings-page-block i-py-14 i-section-divide i-mb-14">
        <div class="settings-page-block-title">
          <h5 class="i-text-heading-5 i-text-neutral-120 i-mb-0">Customize</h5>
          <p class="i-text-body-2 i-text-neutral-110">
            Edit the styling of the Collect email and document portal
          </p>
        </div>
        <div class="settings-page-block-content">
          <router-link
            tabindex="0"
            :to="`/settings/collect/email`"
            class="link d-flex align-items-center justify-content-start i-mb-10"
            href="#"
          >
            <i-featured-icon
              :size="iFeaturedIconSizes.XXL"
              symbol="medium-mail-envelope"
              class="i-mr-6"
            ></i-featured-icon>
            <div class="text">
              <h5>Collect Email</h5>
              <h6>Customize your Collect email</h6>
            </div>
            <icon-hover symbol="small-chevron-right"></icon-hover>
          </router-link>
          <router-link
            tabindex="0"
            :to="`/settings/collect/portal`"
            class="link d-flex align-items-center justify-content-start"
            href="#"
          >
            <i-featured-icon
              :size="iFeaturedIconSizes.XXL"
              symbol="medium-browser"
              class="i-mr-6"
            ></i-featured-icon>
            <div class="text">
              <h5>Collect Portal</h5>
              <h6>Customize your Collect portal</h6>
            </div>
            <icon-hover symbol="small-chevron-right"></icon-hover>
          </router-link>
        </div>
      </div>
    </div>
    <empty-page v-else type="permission"></empty-page>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { isEqual } from 'lodash';
import {
  iButton,
  iTextInput,
  iFeaturedIcon,
  iFeaturedIconSizes,
} from '@inscribe/signal';
import EmptyPage from '@/shared/components/EmptyPage.vue';
import FileUpload from './FileUpload';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';
import IconHover from '@/V2/components/IconHover';

export default {
  components: {
    'empty-page': EmptyPage,
    iButton,
    iFeaturedIcon,
    iTextInput,
    IconHover,
    'settings-section-title': SettingsSectionTitle,
    'file-upload': FileUpload,
  },
  data() {
    return {
      errors: false,
      acceptedFileTypes: ['image/svg+xml'],
      iFeaturedIconSizes,
    };
  },

  computed: {
    ...mapState({
      config: 'collectConfig',
    }),
    ...mapGetters({
      isOrgAdmin: 'isOrgAdmin',
      collectEnabled: 'collectEnabled',
    }),
  },

  watch: {
    'config.data': {
      handler: function (val) {
        this.updateModifiedStatus(!isEqual(val, this.config.initialData));
      },
      deep: true,
    },
    collectEnabled: {
      handler: function (value) {
        if (value) {
          this.loadConfig();
        }
      },
    },
  },

  mounted() {
    this.loadConfig();
  },

  methods: {
    ...mapActions([
      'getCollectConfig',
      'saveCollectConfig',
      'uploadCollectConfigLogo',
      'updateModifiedStatus',
      'resetDataFromInitialState',
    ]),

    loadConfig() {
      if (this.isOrgAdmin && this.collectEnabled) {
        // call get collect config api
        this.getCollectConfig();
      }
    },

    cancel() {
      if (this.config.modified) {
        const answer = window.confirm(
          'Do you really want to undo your unsaved changes?',
        );
        if (answer) {
          // reset to initial state
          this.resetDataFromInitialState();
        }
      }
    },

    save() {
      this.saveCollectConfig(this.config.data);
      this.updateModifiedStatus(!this.config.modified);
    },
  },
};
</script>

<style lang="scss" scoped>
.collect-config {
  label {
    color: $color-almost-black;
    font-weight: bold;

    &:after {
      content: '*';
      margin-left: 5px;
    }

    & + p {
      color: $color-almost-black-80;
      margin: -4px 0 6px 0 !important;
    }
  }

  :deep(.text-input-field) {
    height: 42px;
  }

  .link {
    border: 1px solid $color-almost-black-60;
    padding: 16px 24px;
    border-radius: 12px;

    .text {
      flex-grow: 1;
      h5,
      h6 {
        margin-bottom: 0;
      }

      h5 {
        line-height: 1;
        color: $color-almost-black;
        margin-bottom: 4px;
        font-size: $font-size-default;
        font-weight: 600;
      }

      h6 {
        color: $color-almost-black-80;
        font-size: $font-size-default;
      }
    }

    .chevron {
      flex-shrink: 0;
      color: $color-almost-black-60;
    }

    &:hover,
    &:active {
      border-color: $color-almost-black-80;
      text-decoration: none;
      .chevron {
        color: $color-almost-black-80;
      }
    }
  }

  .buttons {
    button:not(:first-child) {
      margin-left: 12px;
    }
  }
  .icon-wrapper {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    background: $color-navy-blue-10;
    border-radius: 50%;
    margin-right: 12px;
    svg {
      margin-left: 0 !important;
      color: #485d74;
    }
  }

  :deep(.collect-config-portal),
  :deep(.collect-config-email) {
    height: 100vh;
    z-index: 3000;
    background: $color-white;
    position: fixed;
    top: 0;
    left: 240px;
    right: 0;
    bottom: 0;

    @media screen and (max-width: 899px) {
      left: 64px;
    }

    @media screen and (max-width: 767px) {
      left: 0;
    }

    header {
      background: $color-white;
      padding: 16px 24px;
      border-bottom: 1px solid $color-almost-black-60;

      h1,
      h2 {
        font-size: $font-size-default;
        line-height: 1.2;
        margin: 0;
      }

      h1 {
        font-weight: 700;
        margin-bottom: 2px;
      }

      h2 {
        color: $color-almost-black-80;
        font-weight: 400;
      }

      svg {
        color: rgba($color-navy-blue-60, 0.5);
      }

      button:not(:first-child) {
        margin-left: 12px;
      }

      a {
        cursor: pointer;
        color: inherit;
        &:hover,
        &:active {
          text-decoration: none;
          svg {
            color: $color-almost-black-80;
          }
        }
      }
    }

    section {
      flex-grow: 1;
      position: relative;

      aside {
        flex-shrink: 0;
        position: relative;
        align-items: stretch;
        width: 300px;
        border-right: 1px solid $color-almost-black-60;

        label {
          color: $color-almost-black;
          font-size: $font-size-default;

          margin-bottom: 8px;
        }

        input[type='text'],
        textarea {
          outline: 0;
          border-radius: 4px;
          padding: 12px 16px;
          width: 100%;
          font-weight: normal;
          border: 1px solid $color-raven-40;
          &:active,
          &:hover,
          &:focus {
            border: 1px solid $color-raven-80;
          }
        }

        textarea {
          min-height: 150px;
        }

        a {
          cursor: pointer;
          color: $color-navy-blue;
          padding: 24px;
          border-bottom: 1px solid $color-almost-black-60;

          h3 {
            font-weight: 700;
            font-size: $font-size-default;
            margin: 0 0 2px 0;
          }

          span {
            color: $color-almost-black-80;
          }

          svg {
            color: $color-almost-black-60;
          }

          &:hover,
          &:active,
          &:focus {
            text-decoration: none;

            svg {
              color: $color-almost-black-80;
            }
          }
        }
        .slider {
          width: 100%;
          position: absolute;
          top: 0;
          bottom: 0;
          left: -300px;
          background: $color-white;
          -webkit-transition: left 0.3s ease;
          transition: left 0.3s ease;
          padding: 24px;
          overflow-y: auto;

          h4 {
            font-size: $font-size-default;
            font-weight: 700;
            margin-bottom: 40px;
          }
          &.slide-in {
            left: 0;
          }

          .close-button {
            outline: 0;
            position: absolute;
            top: 24px;
            right: 24px;
            border: none;
            background: none;
            svg {
              color: rgba($color-navy-blue-60, 0.5);
            }
            &:hover,
            &:active,
            &:focus {
              svg {
                color: $color-almost-black-80;
              }
            }
          }
        }
      }
      main {
        flex: 1 0 auto;
        position: relative;
      }
    }
  }
}
</style>
